import { makeStyles, PositioningShorthand, PositioningShorthandValue, TeachingPopover, TeachingPopoverBody, TeachingPopoverFooter, TeachingPopoverHeader, TeachingPopoverSurface, TeachingPopoverTitle, TeachingPopoverTrigger } from "@fluentui/react-components";
import { FC, useCallback, useEffect, useState } from "react";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { UserPreferences } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { setUserPreferencesAction } from "../../store/actions/userPreferencesAction";
import { useSurvey, useUserPreferences } from "../../store/hooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { IFeatureHighlights } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { appInsights } from "../../AppInsights";
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_FeatureHighlightDismiss, EventProperty_Value_FeatureHighlightOpen, EventProperty_Value_UploadDocument, UserInteractionEventProperties } from "../../store/models/customEvents";
import { useUserContext } from "../../Contexts/UserContext";

const useStyles = makeStyles({
    popoverSurface: {
        boxShadow: '0 0 10px'
    }
});

// renders the feature highlight popup, which is shown to the user when a new feature is available
// the component needs to be injected in the component that uses the feature
export interface FeatureHighlightPopupProps {
    id: keyof IFeatureHighlights;
    header: string;
    title: string;
    content: string;
    position?: PositioningShorthand;
    link: string;
    children: React.JSX.Element;
}

export const FeatureHighlightPopup: FC<FeatureHighlightPopupProps> = ({ id, header, title, content, position, link, children }: FeatureHighlightPopupProps) => {
    const classes = useStyles();
    const { userPreferences, dispatch } = useUserPreferences();
    const { survey } = useSurvey();
    const { userProfile } = useUserContext();
    const { dispatchNotification } = useNotification();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Do not show any feature highlights if a survey is active, since survey dialog will interfere with the feature highlight dialog.
        // IMPORTANT: the else branch is required — without it, if the feature highlight was already open when the survey
        // arrives (survey is fetched after userPreferences), isOpen would remain true and both would be open simultaneously.
        // In Edge this causes the TeachingPopover's focus trap to block all clicks on the survey CDialog.
        //
        // NOTE: we guard on survey?.id (not just survey) because processGetActiveSurveyForUser can return an empty
        // Survey object {} (truthy) when the server returns 204/null — checking only `survey` would incorrectly
        // suppress the feature highlight even when there is no real active survey.
        if (!survey?.id) {
            const highlightFeature = userPreferences?.featureHighlights?.[id] ?? false;
            console.log(`[FeatureHighlightPopup:${id}] no active survey — featureHighlights.${id}=${highlightFeature} → setIsOpen(${highlightFeature})`);
            setIsOpen(highlightFeature);
            if (highlightFeature) {
                appInsights.trackEvent({
                    name: EventName_UserInteraction, properties:
                        {
                            applicationType: EventProperty_Value_ApplicationType,
                            userId: userProfile?.username,
                            interactionType: EventProperty_Value_FeatureHighlightOpen,
                            interactionValue: id,
                        } as UserInteractionEventProperties
                });
            }
        } else {
            // A real survey (with an id) just became active — close the feature highlight immediately so it doesn't overlap
            console.log(`[FeatureHighlightPopup:${id}] active survey detected (id=${survey.id}) — forcing setIsOpen(false) to prevent overlap`);
            setIsOpen(false);
        }
    }, [userPreferences, survey, id]);

    const onLearnMoreClick = () => {
        console.log(`[FeatureHighlightPopup:${id}] 'Learn more' clicked — opening ${link}`);
        window.open(link, '_blank');
    };

    // when the user dismisses the feature highlight, update the user preferences
    const updateUserPreferences = useCallback(() => {
        // Close immediately so Edge (and all browsers) don't leave the popover open while the API call is in flight,
        // which was causing click events on the Dismiss/X buttons to be swallowed in Edge.
        console.log(`[FeatureHighlightPopup:${id}] dismissed (X or Dismiss button) — setIsOpen(false) immediately, persisting to API`);
        setIsOpen(false);
        performEnterpriseGPTApiCall(async (client) => {
            try {
                const updatedUserPreferences = {
                    ...userPreferences,
                    featureHighlights: {
                        ...userPreferences?.featureHighlights,
                        [id]: false,
                    }
                };
                await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                dispatch(setUserPreferencesAction(updatedUserPreferences));
                appInsights.trackEvent({
                    name: EventName_UserInteraction, properties:
                        {
                            applicationType: EventProperty_Value_ApplicationType,
                            userId: userProfile?.username,
                            interactionType: EventProperty_Value_FeatureHighlightDismiss,
                            interactionValue: id,
                        } as UserInteractionEventProperties
                });
            } catch (e: unknown) {
                if ((e as Error).message !== 'canceled') {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                }
            }
        });
    }, [survey, userPreferences, id]);

    return (
        <TeachingPopover open={isOpen} positioning={position}>
            <TeachingPopoverTrigger>
                <div>
                    {children}
                </div>
            </TeachingPopoverTrigger>
            <TeachingPopoverSurface className={classes.popoverSurface}>
                <TeachingPopoverHeader onClick={updateUserPreferences}>{header}</TeachingPopoverHeader>
                <TeachingPopoverBody>
                    <TeachingPopoverTitle>{title}</TeachingPopoverTitle>
                    <div dangerouslySetInnerHTML={{__html:content}}></div>
                </TeachingPopoverBody>
                <TeachingPopoverFooter primary={{ onClick: onLearnMoreClick, children: "Learn more" }} secondary={{ onClick: updateUserPreferences, children: "Dismiss" }} />
            </TeachingPopoverSurface>
        </TeachingPopover>
    );
}
